/** Header toggle for live-trade desktop alerts. Off by default; clicking it
 * enables (grants OS permission + unlocks audio) or disables. If the browser
 * has hard-blocked notifications, the button is disabled with an explainer. */

import type { ReactElement } from "react";
import { notificationPermission } from "@/lib/notify";
import { useNotifications } from "@/components/Notifications/NotificationsProvider";

export function NotificationBell(): ReactElement {
  const { enabled, enabling, enable, disable } = useNotifications();
  const blocked = !enabled && notificationPermission() === "denied";

  const onClick = (): void => {
    if (enabled) {
      disable();
    } else {
      void enable();
    }
  };

  const { icon, label } = describeState({ enabled, enabling, blocked });

  return (
    <button
      type="button"
      className="btn btn-secondary text-sm"
      onClick={onClick}
      disabled={enabling || blocked}
      aria-pressed={enabled}
      title={
        blocked
          ? "Notifications are blocked in your browser settings — allow them for this site to use alerts."
          : label
      }
    >
      <i className={`ti ${icon} text-base`} />
      {label}
    </button>
  );
}

function describeState({
  enabled,
  enabling,
  blocked,
}: Readonly<{ enabled: boolean; enabling: boolean; blocked: boolean }>): {
  icon: string;
  label: string;
} {
  if (enabling) return { icon: "ti-loader animate-spin", label: "Enabling…" };
  if (blocked) return { icon: "ti-bell-x", label: "Alerts blocked" };
  if (enabled) return { icon: "ti-bell-ringing", label: "Alerts on" };
  return { icon: "ti-bell", label: "Alerts off" };
}
